import { refineRequiredDateTime } from "@features/vessel-call/create/schema/vesselCallFormSchema.ts";
import { normalizeDate, toMinute } from "@utils/date-time";
import { t, type TFunction } from "i18next";
import { z } from "zod";
import { shouldShowVesselDetailRadioButtons } from "../form/movementVesselDetails.helpers";
import { useMovementServiceFormStore } from "../store/useMovementServiceForm.store";

const LANG_BASE = "vessel-call.movement-services";
export const MOVEMENT_SERVICE_REMARKS_MAX_CHARS = 1000;

const required = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

// const optional = (max: number, label = t("form-validation.field")) =>
//   z.string().max(max, t("form-validation.max-length", { label, max })).optional();

const requiredDate = (label = t("form-validation.field")) =>
  z.union([
    z.date({
      required_error: t("form-validation.required", { label }),
      invalid_type_error: t("form-validation.required", { label }),
    }),
    z.string().min(1, t("form-validation.required", { label })),
  ]);

const requiredNotPastDateTime = (label: string) =>
  refineRequiredDateTime(label, "form-validation.must-not-be-in-past")(requiredDate(label));

const optionalDateTime = () => z.union([z.date().nullable().optional(), z.string()]);

const optionalNotPastDateTime = (label: string) =>
  optionalDateTime().superRefine((val, ctx) => {
    if (val == null || val === "") {
      return;
    }

    const valueMinute = toMinute(val);
    const nowMinute = toMinute(new Date());

    if (valueMinute && nowMinute && valueMinute.getTime() < nowMinute.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.must-not-be-in-past", { label }),
      });
    }
  });

type ComparableDateValue = Date | null | string | undefined;

const hasDateTimeValue = (value: ComparableDateValue) => {
  if (value == null) {
    return false;
  }

  return !(typeof value === "string" && value.trim().length === 0);
};

const toComparableDay = (value: ComparableDateValue) => {
  if (!hasDateTimeValue(value)) {
    return null;
  }

  return normalizeDate(value);
};

const ETS_AREA_BERTH_REQUIRED_MOVEMENT_TYPES = new Set([
  "SEA_TO_ANCHORAGE",
  "ANCHORAGE_TO_ANCHORAGE",
]);

export const isMarineRequiredFieldsMovementType = (movementType: string) =>
  ETS_AREA_BERTH_REQUIRED_MOVEMENT_TYPES.has(movementType);

type MovementServiceFormSchemaOptions = {
  canRequestApprovedMovement?: boolean;
};

const ASSOCIATED_PORT_SERVICE_REQUIRED_REASONS = new Set(["ShipToShip", "Bunkering"]);

const H2S_REQUIRED_MOVEMENT_TYPES = new Set([
  "SEA_TO_BERTH",
  "ANCHORAGE_TO_BERTH",
  "BERTH_TO_BERTH",
]);

const shouldRequireH2SDocuments = () => {
  const { movementType, to } = useMovementServiceFormStore.getState();
  return H2S_REQUIRED_MOVEMENT_TYPES.has(movementType) && to === "FOTT";
};

const buildBooleanField = (args: { labelKey: string; t: TFunction }) => {
  const { movementType, from, to } = useMovementServiceFormStore.getState();
  const label = args.t(`${LANG_BASE}.fields.${args.labelKey}`);
  const baseSchema = z.boolean().nullable().optional();

  const shouldRequireField = shouldShowVesselDetailRadioButtons({
    movementType,
    fromLocation: from,
    toLocation: to,
  });

  if (!shouldRequireField) {
    return baseSchema;
  }

  return baseSchema.superRefine((value, ctx) => {
    if (value === null || typeof value === "undefined") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: args.t("form-validation.required", { label }),
      });
    }
  });
};

const DRAFT_MAX_DIGITS = 5;
const DRAFT_MAX_DECIMALS = 3;

const buildDraftField = (args: { labelKey: string; t: TFunction }) => {
  const label = args.t(`${LANG_BASE}.fields.${args.labelKey}`);

  return z
    .preprocess((val) => (typeof val === "number" ? val.toString() : val), z.string().optional())
    .superRefine((val, ctx) => {
      if (!val || val.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: args.t("form-validation.required", { label }),
        });
        return;
      }

      if (!/^\d+(\.\d*)?$/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: args.t("form-validation.invalid-number", { label }),
        });
        return;
      }

      const [intPart, decPart] = val.split(".");

      if (intPart.length > DRAFT_MAX_DIGITS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: args.t("form-validation.max-length-digits", {
            label,
            maxDigits: DRAFT_MAX_DIGITS,
          }),
        });
      }

      if (decPart && decPart.length > DRAFT_MAX_DECIMALS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: args.t("form-validation.max-length-decimals", {
            label,
            maxDecimals: DRAFT_MAX_DECIMALS,
          }),
        });
      }

      const asNumber = Number(val);
      if (Number.isFinite(asNumber) && asNumber <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: args.t("form-validation.must-be-greater", { label, min: 0 }),
        });
      }
    });
};

const buildH2SField = (t: TFunction) =>
  z
    .array(
      z.object({ url: z.string(), certificateType: z.string(), name: z.string(), idx: z.number() }),
    )
    .superRefine((value, ctx) => {
      if (!shouldRequireH2SDocuments()) {
        return;
      }
      if (!Array.isArray(value) || value.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", { label: t(`${LANG_BASE}.sections.h2s`) }),
        });
      }
    });

const buildAdditionalDocumentItemSchema = (t: TFunction) =>
  z
    .object({
      url: z.string(),
      certificateType: z.string(),
      name: z.string(),
      type: z.string().optional(),
      idx: z.number(),
      documentType: z.preprocess((value) => (typeof value === "string" ? value : ""), z.string()),
      otherSpecifyType: z.preprocess(
        (value) => (typeof value === "string" ? value : ""),
        z.string().max(
          30,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.additional-documents.other-specify`),
            max: 30,
          }),
        ),
      ),
      documentName: z.preprocess(
        (value) => (typeof value === "string" ? value : ""),
        z.string().max(
          50,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.additional-documents.document-name`),
            max: 50,
          }),
        ),
      ),
      issueDate: z.union([z.date(), z.string(), z.null()]).optional(),
      expiryDate: z.union([z.date(), z.string(), z.null()]).optional(),
      documentSummary: z.string().optional(),
    })
    .superRefine((val, ctx) => {
      const normalizedDocumentType = val.documentType.trim().toUpperCase();
      const normalizedOtherSpecifyType = val.otherSpecifyType.trim();
      const normalizedDocumentName = val.documentName.trim();
      const issueDateLabel = t(`${LANG_BASE}.additional-documents.issue-date`);
      const expiryDateLabel = t(`${LANG_BASE}.additional-documents.expiry-date`);
      const todayDate = normalizeDate(new Date());

      if (normalizedDocumentType === "OTHER_SPECIFY" && !normalizedOtherSpecifyType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.additional-documents.other-specify`),
          }),
          path: ["otherSpecifyType"],
        });
      }

      if (normalizedDocumentType === "CERTIFICATE" && !val.expiryDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.additional-documents.expiry-date`),
          }),
          path: ["expiryDate"],
        });
      }

      if (!normalizedDocumentName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.additional-documents.document-name`),
          }),
          path: ["documentName"],
        });
      }

      if (!normalizedDocumentType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.additional-documents.document-type`),
          }),
          path: ["documentType"],
        });
      }

      const issueDateDay = toComparableDay(val.issueDate);
      if (issueDateDay && todayDate && issueDateDay.getTime() > todayDate.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-not-be-in-future", {
            label: issueDateLabel,
          }),
          path: ["issueDate"],
        });
      }

      const expiryDateDay = toComparableDay(val.expiryDate);
      if (expiryDateDay && todayDate && expiryDateDay.getTime() < todayDate.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-not-be-in-past", {
            label: expiryDateLabel,
          }),
          path: ["expiryDate"],
        });
      }
    });

export const getMovementServiceFormSchema = (
  t: TFunction,
  { canRequestApprovedMovement = false }: MovementServiceFormSchemaOptions = {},
) =>
  z
    .object({
      from: z.string(),
      fromLabelForValue: z.string().optional(),
      fromAreaBerth: z.string().nullable(),
      to: required(200, t(`${LANG_BASE}.fields.to`)),
      toAreaBerth: z.string().nullable(),
      rts: requiredNotPastDateTime(t(`${LANG_BASE}.fields.rts`)),
      ets: optionalNotPastDateTime(t(`${LANG_BASE}.fields.ets`)),
      movementType: z.string(),
      vesselCondition: required(200, t(`${LANG_BASE}.fields.vessel-condition`)),
      movementReason: required(200, t(`${LANG_BASE}.fields.movement-reason`)),
      associatedPortServiceId: z.string().optional(),
      draftFore: buildDraftField({ labelKey: "draft-fore", t }),
      draftAft: buildDraftField({ labelKey: "draft-aft", t }),
      hasOilStains: buildBooleanField({ labelKey: "has-oil-stains", t }),
      hasProtrusionsOrOverhand: buildBooleanField({ labelKey: "has-protrusions", t }),
      additionalResources: z
        .array(
          z.object({
            additionalResourceType: z.string().optional(),
            numberOfResources: z
              .string()
              .optional()
              .superRefine((val, ctx) => {
                if (!val || val.trim() === "") {
                  return;
                }

                const label = t(`${LANG_BASE}.fields.number-of-resources`);
                const trimmedVal = val.trim();

                if (trimmedVal.includes(".") || trimmedVal.includes(",")) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${label} must be an integer (no decimals).`,
                  });
                  return;
                }

                const num = parseInt(trimmedVal, 10);
                const isInteger = !isNaN(num) && num.toString() === trimmedVal;

                if (!isInteger) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${label} must be an integer.`,
                  });
                  return;
                }

                if (num < 1) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${label} must be a positive number.`,
                  });
                  return;
                }

                if (trimmedVal.length > 4) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("form-validation.must-have-maximum-digits", {
                      label,
                      max: 4,
                    }),
                  });
                }
              }),
          }),
        )
        .optional(),
      additionalDocuments: z.array(buildAdditionalDocumentItemSchema(t)).optional(),
      h2s: buildH2SField(t),
      pilotRequestNotification: z
        .array(
          z.object({
            url: z.string(),
            certificateType: z.string(),
            name: z.string(),
            idx: z.number(),
          }),
        )
        .optional(),
      comments: z
        .string()
        .max(
          MOVEMENT_SERVICE_REMARKS_MAX_CHARS,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.fields.comments`),
            max: MOVEMENT_SERVICE_REMARKS_MAX_CHARS,
          }),
        )
        .optional(),
      vesselId: z.string(),
      vesselCallId: z.string(),
    })
    .superRefine((data, ctx) => {
      if (
        ["SEA_TO_BERTH", "ANCHORAGE_TO_BERTH", "BERTH_TO_BERTH"].includes(data.movementType) &&
        !data.pilotRequestNotification?.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["pilotRequestNotification"],
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.sections.pilot-request-notification`),
          }),
        });
      }

      if (
        ASSOCIATED_PORT_SERVICE_REQUIRED_REASONS.has(data.movementReason) &&
        !data.associatedPortServiceId?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["associatedPortServiceId"],
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.fields.associated-port-service`),
          }),
        });
      }

      const requiresMarineFields =
        canRequestApprovedMovement && isMarineRequiredFieldsMovementType(data.movementType);

      if (
        canRequestApprovedMovement &&
        data.movementType === "ANCHORAGE_TO_ANCHORAGE" &&
        !data.toAreaBerth?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["toAreaBerth"],
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.fields.to-area-berth`),
          }),
        });
      }

      if (requiresMarineFields && !hasDateTimeValue(data.ets)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ets"],
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.fields.ets`),
          }),
        });
      }

      if (hasDateTimeValue(data.ets) && hasDateTimeValue(data.rts)) {
        const rtsMinute = toMinute(data.rts);
        const etsMinute = toMinute(data.ets);
        if (rtsMinute && etsMinute && etsMinute < rtsMinute) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ets"],
            message: t("form-validation.must-be-after", {
              subject: t(`${LANG_BASE}.fields.ets`),
              object: t(`${LANG_BASE}.fields.rts`),
            }),
          });
        }
      }
    });

export type MovementServiceFormData = z.infer<ReturnType<typeof getMovementServiceFormSchema>>;
